import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";

import { db } from "../firebase";

function getOrCreateDeviceId() {
  try {
    const existing = localStorage.getItem("pp:adminDeviceId");
    if (existing) return existing;

    const next =
      "dev_" +
      (window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now());

    localStorage.setItem("pp:adminDeviceId", next);
    return next;
  } catch {
    return `dev_${Math.random().toString(36).slice(2)}_${Date.now()}`;
  }
}

function getSessionFingerprint() {
  const ua = navigator.userAgent || "";

  let browser = "Unknown";
  if (ua.includes("Edg")) browser = "Edge";
  else if (ua.includes("Chrome")) browser = "Chrome";
  else if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Safari")) browser = "Safari";

  let platform = "Unknown";
  if (ua.includes("Windows")) platform = "Windows";
  else if (ua.includes("Mac")) platform = "MacOS";
  else if (ua.includes("Android")) platform = "Android";
  else if (ua.includes("iPhone")) platform = "iPhone";

  const deviceId = getOrCreateDeviceId();

  return {
    deviceId,
    browser,
    platform,
    userAgent: ua,
    screen: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Unknown",
    language: navigator.language || "Unknown",
  };
}

export function getStoredAdminSessionId() {
  try {
    return localStorage.getItem("pp:adminSessionId");
  } catch {
    return null;
  }
}

export function clearStoredAdminSessionId() {
  try {
    localStorage.removeItem("pp:adminSessionId");
  } catch {}
}

export async function getAdminSession({ schoolId, sessionId }) {
  if (!schoolId || !sessionId) return null;

  const ref = doc(db, "schools", schoolId, "adminSessions", sessionId);
  const snap = await getDoc(ref);

  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

async function registerTrustedDevice({
  uid,
  schoolId,
  email,
  displayName,
  fingerprint,
}) {
  const trustedDeviceId = `${uid}_${fingerprint.deviceId}`;

  const ref = doc(
    db,
    "schools",
    schoolId,
    "adminTrustedDevices",
    trustedDeviceId
  );

  const snap = await getDoc(ref);
  const isNewDevice = !snap.exists();

  if (isNewDevice) {
    await setDoc(ref, {
      uid,
      schoolId,
      email: email || "",
      displayName: displayName || "",
      trustedDeviceId,
      deviceId: fingerprint.deviceId,
      browser: fingerprint.browser,
      platform: fingerprint.platform,
      userAgent: fingerprint.userAgent,
      screen: fingerprint.screen,
      timezone: fingerprint.timezone,
      language: fingerprint.language,
      trusted: true,
      firstSeenAt: serverTimestamp(),
      lastSeenAt: serverTimestamp(),
    });

    const eventRef = doc(collection(db, "schools", schoolId, "adminDeviceEvents"));

    await setDoc(eventRef, {
      type: "new_admin_device_detected",
      action: "new_admin_device_detected",
      severity: "medium",
      uid,
      schoolId,
      email: email || "",
      displayName: displayName || "",
      trustedDeviceId,
      deviceId: fingerprint.deviceId,
      browser: fingerprint.browser,
      platform: fingerprint.platform,
      timezone: fingerprint.timezone,
      createdAt: serverTimestamp(),
    });
  } else {
    await updateDoc(ref, {
      lastSeenAt: serverTimestamp(),
      browser: fingerprint.browser,
      platform: fingerprint.platform,
      screen: fingerprint.screen,
      timezone: fingerprint.timezone,
      language: fingerprint.language,
    });
  }

  return { trustedDeviceId, isNewDevice };
}

export async function createAdminSession({
  uid,
  schoolId,
  email,
  displayName,
  mfaVerified = true,
}) {
  if (!uid || !schoolId) return null;

  const existingSessionId = getStoredAdminSessionId();

  if (existingSessionId) {
    const existing = await getAdminSession({
      schoolId,
      sessionId: existingSessionId,
    });

    if (existing && existing.revoked !== true) {
      await updateAdminSessionHeartbeat({
        schoolId,
        sessionId: existingSessionId,
      });

      return existingSessionId;
    }

    clearStoredAdminSessionId();
  }

  const sessionId = `sess_${Math.random().toString(36).slice(2)}_${Date.now()}`;
  const fp = getSessionFingerprint();

  const trusted = await registerTrustedDevice({
    uid,
    schoolId,
    email,
    displayName,
    fingerprint: fp,
  });

  const ref = doc(collection(db, "schools", schoolId, "adminSessions"), sessionId);

  await setDoc(ref, {
    uid,
    schoolId,
    email: email || "",
    displayName: displayName || "",
    sessionId,

    deviceId: fp.deviceId,
    trustedDeviceId: trusted.trustedDeviceId,
    isNewDevice: trusted.isNewDevice,

    browser: fp.browser,
    platform: fp.platform,
    userAgent: fp.userAgent,
    screen: fp.screen,
    timezone: fp.timezone,
    language: fp.language,

    mfaVerified,
    revoked: false,
    current: true,

    createdAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
  });

  localStorage.setItem("pp:adminSessionId", sessionId);

  return sessionId;
}

export async function updateAdminSessionHeartbeat({ schoolId, sessionId }) {
  if (!schoolId || !sessionId) return null;

  const session = await getAdminSession({ schoolId, sessionId });

  if (!session || session.revoked === true) {
    return { revoked: true };
  }

  const ref = doc(db, "schools", schoolId, "adminSessions", sessionId);

  await updateDoc(ref, {
    lastSeenAt: serverTimestamp(),
    current: true,
  });

  return { revoked: false };
}

export async function revokeAdminSession({ schoolId, sessionId, revokedBy }) {
  if (!schoolId || !sessionId) return;

  const ref = doc(db, "schools", schoolId, "adminSessions", sessionId);

  await updateDoc(ref, {
    revoked: true,
    current: false,
    revokedBy: revokedBy || "",
    revokedAt: serverTimestamp(),
  });
}